import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'xinggan-chuanda');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 md:pt-18">
        <Breadcrumb
          items={[
            { label: currentCategory?.name || '分类', href: `/category/${activeCategory}` },
          ]}
        />

        {/* Category Header */}
        <section className="relative w-full min-h-[280px] md:min-h-[340px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20and%20beauty%20lifestyle%20concept%20with%20soft%20textures%20and%20warm%20lighting%2C%20minimalist%20aesthetic%2C%20cream%20and%20rose%20gold%20tones%2C%20editorial%20photography%20style&width=1600&height=700&seq=category-header-01&orientation=landscape"
            alt={currentCategory?.name || '分类'}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35" />
          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {currentCategory?.name || '分类'}
            </h1>
            <p className="text-sm md:text-base text-white/85 max-w-xl mx-auto">
              {currentCategory?.description || ''}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200/70 bg-background-50 sticky top-16 md:top-18 z-30">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 md:py-4 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => {
                    setActiveCategory(cat.slug);
                    window.history.pushState(null, '', `/category/${cat.slug}`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`} />
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <p className="text-sm text-foreground-600">
                共 <strong className="text-foreground-900">{categoryArticles.length}</strong> 篇文章
              </p>
            </div>
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} {...article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl" />
                </div>
                <p className="text-foreground-600">该分类暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${currentCategory?.name || '分类'} - 美女性感魅力养成指南`,
          "description": currentCategory?.description || '',
          "url": `https://yourdomain.com/category/${activeCategory}`,
          "inLanguage": "zh-CN",
          "isPartOf": {
            "@type": "WebSite",
            "name": "美女性感魅力养成指南",
            "url": "https://yourdomain.com/"
          }
        })
      }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://yourdomain.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": currentCategory?.name || '分类',
              "item": `https://yourdomain.com/category/${activeCategory}`
            }
          ]
        })
      }} />
    </>
  );
}